import { Empty, Flex, Grid, Skeleton, Table, Tooltip, Typography } from 'antd'
import type { ColumnType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { formatDate, formatDateTime, formatNumber } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { LoadError } from '@/shared/ui/LoadError'
import { VoucherAwaiting } from './VoucherAwaiting'
import type { BatchesState } from './useVoucherStock'
import {
  isEndpointMissing,
  MY_BATCHES_ENDPOINT,
  VOUCHER_STATES,
  type VoucherBatchRow,
} from './voucherStockApi'

/**
 * Holat bo'yicha sanoq — BITTA ustunda, beshta son.
 *
 * ⚠️ HAR HOLAT ALOHIDA USTUN QILINMADI: telefonda beshta tor ustun
 * o'qib bo'lmaydigan darajada siqilardi, kompyuterda esa jadval o'n
 * ustunga cho'zilardi. Bu yerda ular bitta qatorda, YORLIQ bilan
 * turadi.
 *
 * ⛔ `null` — server sanoqni BERMADI, «hammasi nol» EMAS. Nolga
 * qaytarish partiyani butunlay sotilmagan qilib ko'rsatardi va bu
 * ishonarli ko'rinardi. Shu holatda chiziqcha chiziladi.
 *
 * ⚠️ NOL KO'RSATILADI, yashirilmaydi: «`ACTIVATED: 0`» — haqiqiy javob
 * («hali hech kim ishlatmagan»), qatorning umuman yo'qligi esa
 * «noma'lum» bo'lib o'qilardi. Ikkalasi boshqa gap.
 */
function StateCounts({ batch }: { batch: VoucherBatchRow }) {
  const { t } = useTranslation()
  if (batch.counts === null) return <>{t('common.dash')}</>

  return (
    <Flex gap={10} wrap>
      {VOUCHER_STATES.map((state) => (
        <Tooltip key={state} title={t(`myVouchers.state.${state}`)}>
          <Typography.Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t(`myVouchers.stateShort.${state}`)}{' '}
            </Typography.Text>
            {formatNumber(batch.counts?.[state] ?? 0)}
          </Typography.Text>
        </Tooltip>
      ))}
    </Flex>
  )
}

interface MyBatchesTableProps {
  state: BatchesState
  /** Qatorni bosganda kodlar oynasi ochiladi */
  onSelect: (batch: VoucherBatchRow) => void
  /** Paketning foydalanuvchi tilidagi nomi; `null` — nom topilmadi */
  planLabel: (batch: VoucherBatchRow) => string | null
  /** Qidiruv yozilganmi — bo'sh natijaning IKKI sababi ARALASHTIRILMAYDI */
  filtered: boolean
}

/**
 * Sherikka berilgan partiyalar jadvali.
 *
 * ⚠️ QATOR BOSILADIGAN, «amal ustuni» YO'Q: bosish faqat KO'RSATADI
 * (kodlar oynasini ochadi) va hech narsani o'zgartirmaydi, ya'ni
 * tasodifiy tegish zararsiz. Ayni qaror `OperationsList` da ham bor.
 *
 * ⛔ BU YERDA PUL YIG'INDISI YO'Q. Partiyaning narxi ustunda ko'rinadi,
 * lekin «jami qancha» degan qator qo'shilmaydi: vaucher ledgerga
 * umuman tushmaydi (egasi qarori) va yig'indi sherik uchun DAROMAD
 * bo'lib o'qilardi — holbuki bu faqat qog'ozdagi nominal.
 */
export function MyBatchesTable({ state, onSelect, planLabel, filtered }: MyBatchesTableProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false

  if (state.error && isEndpointMissing(state.error)) {
    return <VoucherAwaiting endpoint={MY_BATCHES_ENDPOINT} note={t('myVouchers.awaitingBatches')} />
  }
  if (state.error) {
    return <LoadError titleKey="myVouchers.batchesFailed" error={state.error} />
  }
  if (state.loading) {
    return <Skeleton active paragraph={{ rows: 4 }} />
  }

  const rows = state.page?.rows ?? []
  if (rows.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={filtered ? t('myVouchers.emptyFiltered') : t('myVouchers.empty')}
      >
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {filtered ? t('myVouchers.emptyFilteredHint') : t('myVouchers.emptyHint')}
        </Typography.Text>
      </Empty>
    )
  }

  /**
   * ⚠️ Raqam yo'q bo'lsa CHIZIQCHA, `id` ning qisqartmasi EMAS
   * (`voucherStockApi.batchNo` izohi): qisqartma raqamga o'xshab turadi
   * va sherik uni operatorga aytib yuborardi.
   */
  const noText = (batch: VoucherBatchRow) =>
    batch.batchNo === null ? t('common.dash') : `#${formatNumber(batch.batchNo)}`

  const hint = (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {t('myVouchers.rowHint')}
    </Typography.Text>
  )

  // Telefonda jadval emas, kartochka — yetti ustun 390px da bir-biriga
  // yopishadi (`OperationsList` bilan ayni qaror).
  if (isMobile) {
    return (
      <Flex vertical gap={8}>
        {hint}
        {rows.map((batch) => (
          <div
            key={batch.id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(batch)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelect(batch)
              }
            }}
            style={{
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              paddingBottom: 8,
              cursor: 'pointer',
            }}
          >
            <Flex justify="space-between" align="baseline" gap={8}>
              <Typography.Text strong>{noText(batch)}</Typography.Text>
              <Typography.Text strong>{formatMoneyValue(batch.price)}</Typography.Text>
            </Flex>
            <div
              style={{
                fontSize: 13,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {planLabel(batch) ?? t('common.dash')}
            </div>
            {batch.note ? (
              <Typography.Text type="secondary" dir="auto" style={{ fontSize: 12 }}>
                {batch.note}
              </Typography.Text>
            ) : null}
            <StateCounts batch={batch} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('myVouchers.col.quantity')}: {formatNumber(batch.quantity)}
              {batch.validUntil
                ? ` · ${t('myVouchers.col.validUntil')}: ${formatDate(batch.validUntil)}`
                : ''}
            </Typography.Text>
          </div>
        ))}
      </Flex>
    )
  }

  const columns: ColumnType<VoucherBatchRow>[] = [
    { title: t('myVouchers.col.batchNo'), render: (_v, b) => noText(b), width: 90 },
    {
      title: t('myVouchers.col.note'),
      render: (_v, b) => (b.note ? <span dir="auto">{b.note}</span> : t('common.dash')),
      ellipsis: true,
      width: 160,
    },
    {
      title: t('myVouchers.col.plan'),
      render: (_v, b) => planLabel(b) ?? t('common.dash'),
      ellipsis: true,
    },
    {
      title: t('myVouchers.col.quantity'),
      align: 'right',
      render: (_v, b) => formatNumber(b.quantity),
      width: 90,
    },
    { title: t('myVouchers.col.counts'), render: (_v, b) => <StateCounts batch={b} />, width: 260 },
    {
      title: t('myVouchers.col.price'),
      align: 'right',
      render: (_v, b) => formatMoneyValue(b.price),
      width: 130,
    },
    {
      // ⛔ Bu PARTIYANING javon muddati, kartaning `expiresAt` i EMAS
      // (sxema izohi). Yorlig'i ham aynan shuni aytadi.
      title: t('myVouchers.col.validUntil'),
      render: (_v, b) => (b.validUntil ? formatDate(b.validUntil) : t('common.dash')),
      width: 120,
    },
    {
      title: t('common.createdAt'),
      render: (_v, b) => formatDateTime(b.createdAt),
      width: 170,
    },
  ]

  return (
    <Flex vertical gap={12}>
      {hint}
      <Table<VoucherBatchRow>
        dataSource={rows}
        rowKey={(b) => b.id}
        pagination={false}
        size="small"
        scroll={{ x: true }}
        onRow={(b) => ({ onClick: () => onSelect(b), style: { cursor: 'pointer' } })}
        columns={columns}
      />
    </Flex>
  )
}
